<script setup>
import { ref, computed } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { FormKit } from '@formkit/vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const { integration, hasConnectedHooks } = useIntegrationHook('proxy');
const accountId = getters.getCurrentAccountId;

const showAddModal = ref(false);
const showDeleteModal = ref(false);
const showEditModal = ref(false);
const selectedHook = ref(null);
const editValues = ref({});
const isUpdating = ref(false);
const isCreating = ref(false);
const addValues = ref({});

const hooks = computed(() => {
  if (!hasConnectedHooks.value) return [];
  return integration.value.hooks || [];
});

const formItems = computed(() => integration.value.settings_form_schema || []);

const redirectUrlFor = hookId =>
  `${window.location.origin}/api/v1/accounts/${accountId.value}/integrations/proxy/redirect/${hookId}`;

const profileUrl = computed(() =>
  frontendURL(`accounts/${accountId.value}/profile/settings`)
);

function copyUrl(hookId) {
  navigator.clipboard.writeText(redirectUrlFor(hookId));
  useAlert(t('INTEGRATION_APPS.PROXY.COPY_SUCCESS'));
}

function openEdit(hook) {
  selectedHook.value = hook;
  editValues.value = { ...hook.settings };
  showEditModal.value = true;
}

function openDelete(hook) {
  selectedHook.value = hook;
  showDeleteModal.value = true;
}

async function submitEdit() {
  isUpdating.value = true;
  try {
    await store.dispatch('integrations/updateHook', {
      hookId: selectedHook.value.id,
      appId: 'proxy',
      settings: editValues.value,
    });
    useAlert(t('INTEGRATION_APPS.PROXY.EDIT_SUCCESS'));
    showEditModal.value = false;
  } catch {
    useAlert(t('INTEGRATION_APPS.PROXY.EDIT_ERROR'));
  } finally {
    isUpdating.value = false;
  }
}

async function submitAdd() {
  isCreating.value = true;
  try {
    await store.dispatch('integrations/createHook', {
      app_id: 'proxy',
      settings: addValues.value,
    });
    useAlert(t('INTEGRATION_APPS.ADD.API.SUCCESS_MESSAGE'));
    showAddModal.value = false;
    addValues.value = {};
  } catch {
    useAlert(t('INTEGRATION_APPS.ADD.API.ERROR_MESSAGE'));
  } finally {
    isCreating.value = false;
  }
}

async function confirmDelete() {
  try {
    await store.dispatch('integrations/deleteHook', {
      hookId: selectedHook.value.id,
      appId: 'proxy',
    });
    useAlert(t('INTEGRATION_APPS.DELETE.API.SUCCESS_MESSAGE'));
    showDeleteModal.value = false;
  } catch {
    useAlert(t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE'));
  }
}
</script>

<template>
  <div class="flex flex-col flex-1 gap-8 overflow-auto">
    <BaseSettingsHeader
      :title="$t('INTEGRATION_APPS.PROXY.TITLE')"
      :description="$t('INTEGRATION_APPS.PROXY.DESCRIPTION')"
      feature-name="proxy"
      :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
    >
      <template #actions>
        <Button
          icon="i-lucide-circle-plus"
          :label="$t('INTEGRATION_APPS.ADD_BUTTON')"
          @click="showAddModal = true"
        />
      </template>
    </BaseSettingsHeader>

    <div class="w-full">
      <table v-if="hasConnectedHooks" class="w-full">
        <thead>
          <tr
            class="[&>th]:font-semibold [&>th]:tracking-[1px] [&>th]:text-left [&>th]:px-2.5 [&>th]:uppercase [&>th]:text-n-slate-12 [&>th]:text-xs"
          >
            <th class="!pl-0">
              {{ $t('INTEGRATION_APPS.PROXY.COLUMNS.NAME') }}
            </th>
            <th>{{ $t('INTEGRATION_APPS.PROXY.COLUMNS.ENDPOINT') }}</th>
            <th>{{ $t('INTEGRATION_APPS.PROXY.COLUMNS.URL') }}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="hook in hooks"
            :key="hook.id"
            class="border-b border-n-weak [&>td]:py-3 [&>td]:px-2.5 [&>td]:text-n-slate-12 [&>td]:text-sm"
          >
            <td class="!pl-0 font-medium">{{ hook.settings.name || '—' }}</td>
            <td class="font-mono text-xs">
              {{ hook.settings.redirect_endpoint }}
            </td>
            <td>
              <div class="flex items-center gap-2">
                <span
                  class="font-mono text-xs text-n-slate-10 truncate max-w-xs"
                >
                  {{ redirectUrlFor(hook.id) }}
                </span>
                <Button
                  v-tooltip.top="$t('INTEGRATION_APPS.PROXY.COPY_URL')"
                  icon="i-lucide-copy"
                  xs
                  faded
                  slate
                  @click="copyUrl(hook.id)"
                />
              </div>
            </td>
            <td>
              <div class="flex justify-end gap-1">
                <Button
                  v-tooltip.top="$t('INTEGRATION_APPS.PROXY.EDIT')"
                  icon="i-lucide-pencil"
                  xs
                  faded
                  slate
                  @click="openEdit(hook)"
                />
                <Button
                  v-tooltip.top="$t('INTEGRATION_APPS.LIST.DELETE.BUTTON_TEXT')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openDelete(hook)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-else
        class="flex flex-col items-center justify-center py-12 text-n-slate-10"
      >
        <p>
          {{
            $t('INTEGRATION_APPS.NO_HOOK_CONFIGURED', {
              integrationId: 'proxy',
            })
          }}
        </p>
      </div>
    </div>

    <!-- Add modal -->
    <woot-modal
      v-model:show="showAddModal"
      :on-close="() => (showAddModal = false)"
    >
      <div class="flex flex-col h-auto overflow-auto integration-hooks">
        <woot-modal-header
          :header-title="integration.name"
          :header-content="integration.short_description"
        />
        <p class="px-8 pb-2 text-xs text-n-slate-10">
          {{ $t('INTEGRATION_APPS.PROXY.DESCRIPTION') }}
          <a
            :href="profileUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-blue-9 underline ml-1"
          >
            {{ $t('INTEGRATION_APPS.PROXY.PROFILE_LINK') }}
          </a>
        </p>
        <FormKit
          v-model="addValues"
          type="form"
          form-class="w-full grid gap-4 px-8 pb-6"
          :submit-attrs="{ inputClass: 'hidden', wrapperClass: 'hidden' }"
          :incomplete-message="false"
          @submit="submitAdd"
        >
          <FormKit v-for="item in formItems" :key="item.name" v-bind="item" />
          <div class="flex flex-row justify-end w-full gap-2 py-2">
            <Button
              faded
              slate
              :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
              @click.prevent="showAddModal = false"
            />
            <Button
              type="submit"
              :label="$t('INTEGRATION_APPS.ADD.FORM.SUBMIT')"
              :is-loading="isCreating"
            />
          </div>
        </FormKit>
      </div>
    </woot-modal>

    <!-- Edit modal -->
    <woot-modal
      v-model:show="showEditModal"
      :on-close="() => (showEditModal = false)"
    >
      <div class="flex flex-col h-auto overflow-auto integration-hooks">
        <woot-modal-header
          :header-title="$t('INTEGRATION_APPS.PROXY.EDIT_TITLE')"
        />
        <FormKit
          v-model="editValues"
          type="form"
          form-class="w-full grid gap-4 px-8 pb-6"
          :submit-attrs="{ inputClass: 'hidden', wrapperClass: 'hidden' }"
          :incomplete-message="false"
          @submit="submitEdit"
        >
          <FormKit v-for="item in formItems" :key="item.name" v-bind="item" />
          <div class="flex flex-row justify-end w-full gap-2 py-2">
            <Button
              faded
              slate
              :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
              @click.prevent="showEditModal = false"
            />
            <Button
              type="submit"
              :label="$t('INTEGRATION_APPS.ADD.FORM.SUBMIT')"
              :is-loading="isUpdating"
            />
          </div>
        </FormKit>
      </div>
    </woot-modal>

    <!-- Delete modal -->
    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="confirmDelete"
      :title="$t('INTEGRATION_APPS.DELETE.TITLE.ACCOUNT')"
      :message="$t('INTEGRATION_APPS.DELETE.MESSAGE.ACCOUNT')"
      :confirm-text="$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.ACCOUNT')"
      :reject-text="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </div>
</template>
